import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { Price } from 'src/api/endpoints/cloudConnect.types';
import {
  useChangeEnterpriseSubscriptionBillingInterval,
  useEnterpriseSubscription,
} from 'src/api/queries/enterpriseSubscription';
import { Button, Divider, Modal, NotificationBox } from 'src/components';
import { PriceConnect } from 'src/components/PriceConnect';
import { formatDate } from 'src/utils/formatDate';

export const BILLING_ROUTE_PATH = '/_auth/enterprise/_with-org/_layout/billing';

const billingRouteApi = getRouteApi(BILLING_ROUTE_PATH);

export function ChangePlanMonthlyModal() {
  const search = billingRouteApi.useSearch();
  const navigate = billingRouteApi.useNavigate();
  const subscription = useEnterpriseSubscription();
  const changeBillingInterval =
    useChangeEnterpriseSubscriptionBillingInterval();

  const currentAmountOfSeats =
    subscription.data?.planCurrent.prices.monthly.quantity || 1;

  const newPrice = useMemo(() => {
    if (!subscription.data) return null;
    const monthlyPlan = subscription.data.planOptions.find(
      (plan) => plan.interval === 'monthly'
    );
    if (!monthlyPlan) return null;
    const newQuantity = currentAmountOfSeats;
    return {
      ...monthlyPlan.prices.monthly,
      quantity: newQuantity,
      totalAmount: monthlyPlan.prices.monthly.unitAmount * newQuantity,
    } satisfies Price;
  }, [subscription.data]);

  const monthlyPlan = useMemo(() => {
    if (!subscription.data) return null;
    return subscription.data.planOptions.find(
      (plan) => plan.interval === 'monthly'
    );
  }, [subscription.data]);

  if (!monthlyPlan) return null;

  return (
    <Modal.Content className="flex flex-col gap-24 text-left">
      <Modal.Title asChild>
        <Typography as="h2" variant="heading-md" className="text-center">
          Switch to monthly plan
        </Typography>
      </Modal.Title>

      <Modal.Description asChild>
        <Typography variant="body-md">
          This plan automatically adjusts the number of seats every month. Your
          plan change will take effect at the end of your current billing
          period.
        </Typography>
      </Modal.Description>

      <Divider />

      {search.switchToMonthlyPlan === 'confirm' && (
        <div className="flex flex-col gap-16">
          <table className="max-w-sm gap-4">
            <tbody>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Current seats:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    {currentAmountOfSeats}
                  </Typography>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Price per seat:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    <PriceConnect
                      price={monthlyPlan.prices.monthly}
                      variant="unit"
                    />
                  </Typography>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Total price:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    <PriceConnect price={newPrice} variant="total" />
                  </Typography>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <Typography variant="body-md">Starting date:</Typography>
                </th>
                <td>
                  <Typography variant="body-md" className="font-semibold">
                    {formatDate(subscription.data?.periodEnd)}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </table>

          <Divider className="mt-12" />

          <div className="mt-24 flex w-full flex-col justify-center gap-16 ls:flex-row">
            <Button
              variant="primary"
              size="medium"
              loading={changeBillingInterval.isPending}
              onClick={() =>
                changeBillingInterval.mutate(
                  {
                    interval: 'monthly',
                    seats: undefined, // monthly plan does not accept
                  },
                  {
                    onSuccess: () => {
                      return navigate({
                        to: '.',
                        search: (prev) => ({
                          ...prev,
                          switchToMonthlyPlan: undefined,
                        }),
                      });
                    },
                  }
                )
              }
              data-cy="confirm-monthly-plan-change-button"
              mobileFullWidth
              analytics={{
                text: 'Confirm',
                location: ComponentLocations.ENTERPRISE.MODAL_SWITCH_TO_MONTHLY,
                action: 'confirm switch to monthly plan',
              }}
            >
              Confirm
            </Button>
          </div>

          <NotificationBox error={changeBillingInterval.error} />
        </div>
      )}
    </Modal.Content>
  );
}
